'use client'
import { SettingsType } from '@/config/global-config'
import { useColorScheme } from '@mui/material'
import { useTheme } from 'next-themes'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
type SettingsContext = {
  values: SettingsType
  setValues: (values: SettingsType) => void
  toggleTheme: () => void
}

export const SettingsContext = createContext<SettingsContext | undefined>(undefined)

// Helper function to get system preference
const getSystemPreference = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export function SettingsProvider({
  children,
  defaultSettings,
}: {
  children: React.ReactNode
  defaultSettings: SettingsType
}) {
  // logic này để lưu theme vào local storage, hiện tại chưa có tính năng chuyển màu nên ko cần lưu
  //   const [values, setValues] = useLocalStorage<SettingsType>('settings', {
  //     ...defaultSettings,
  //     mode: getSystemPreference(), // Set initial mode based on system preference
  //   })
  const [values, setValues] = useState<SettingsType>(defaultSettings)

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const storedSettings = localStorage.getItem('settings')
      if (!storedSettings) {
        setValues((prev: SettingsType) => ({
          ...prev,
          mode: e.matches ? 'dark' : 'light',
        }))
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [setValues])

  const toggleTheme = useCallback(() => {
    setValues((prev: SettingsType) => ({
      ...prev,
      mode: prev.mode === 'light' ? 'dark' : 'light',
    }))
  }, [setValues])

  const memoizedValue = useMemo(
    () => ({
      values,
      setValues,
      toggleTheme,
    }),
    [values, setValues, toggleTheme]
  )

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>
}
export function useSettingsContext() {
  const context = useContext(SettingsContext)

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider')

  return context
}
